import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Grid, Tooltip, InputAdornment, Typography } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';

import { generateWtwSecurityId } from '../../security-id-utils';
import { ApplicationFormState } from '../../types';
import { BirthDateFields } from './BirthDateFields';

interface WtwSocialSecurityIdProps {
  job: Job;
  showOnlyRequired?: boolean;
}

/**
 * Hook to automatically sync the WTW security ID field when separate components change
 */
export const useWtwSecurityIdSync = () => {
  const { values, setFieldValue } = useFormikContext<ApplicationFormState>();

  useEffect(() => {
    const { firstName, lastName } = values.basicInfo;
    const { monthOfBirth, dayOfBirth, ssnOrPassportLast3Digits } =
      values.details;

    // Only generate if all required fields are present
    if (
      firstName &&
      lastName &&
      monthOfBirth > 0 &&
      dayOfBirth > 0 &&
      ssnOrPassportLast3Digits &&
      ssnOrPassportLast3Digits.length === 3
    ) {
      const securityId = generateWtwSecurityId(
        firstName,
        lastName,
        monthOfBirth,
        dayOfBirth,
        ssnOrPassportLast3Digits,
      );

      // Update both security ID and confirm security ID fields
      if (securityId !== values.details.securityId) {
        setFieldValue('details.securityId', securityId);
        setFieldValue('details.confirmSecurityId', securityId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.basicInfo.firstName,
    values.basicInfo.lastName,
    values.details.monthOfBirth,
    values.details.dayOfBirth,
    values.details.ssnOrPassportLast3Digits,
    values.details.securityId,
    setFieldValue,
  ]);
};

/**
 * WTW-specific Social Security ID form component
 * This component renders a custom form for WTW job applications
 * Format: DDMMFLXXX (DD + MM + F initial + L initial + last 3 digits of SSN/Passport)
 */
export const WtwSocialSecurityId: React.FC<WtwSocialSecurityIdProps> = ({
  job,
  showOnlyRequired = false,
}) => {
  useWtwSecurityIdSync();
  const { t } = useTranslation('jobForm');

  return (
    <>
      <Grid item>
        <Typography variant="h6" gutterBottom>
          {t('applicationForm.socialSecurity.title')}{' '}
          <Typography component="span" color="error">
            *
          </Typography>
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {t('applicationForm.socialSecurity.wtwFormatDescription')}
        </Typography>
      </Grid>

      {/* Birth date fields - reused component */}
      <Grid item>
        <BirthDateFields showOnlyRequired={showOnlyRequired} />
      </Grid>

      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>(
            (i) => i.details.ssnOrPassportLast3Digits,
          )}
          fullWidth
          variant="filled"
          label={t('applicationForm.socialSecurity.last3SSN')}
          placeholder="789"
          inputProps={{ maxLength: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    <div>
                      <strong>
                        {t('applicationForm.socialSecurity.wtwLast3TooltipTitle')}
                      </strong>
                      <br />
                      <br />
                      {t('applicationForm.socialSecurity.wtwLast3TooltipBody')}
                    </div>
                  }
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>((i) => i.details.securityId)}
          fullWidth
          variant="filled"
          label={t('applicationForm.socialSecurity.securityId')}
          disabled
          placeholder="DDMMFLXXX"
          inputProps={{ maxLength: 9 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    <div>
                      <strong>{t('applicationForm.socialSecurity.wtwSecurityIdFormatTitle')}</strong>
                      <br />
                      <br />
                      <strong>DD</strong> – {t('applicationForm.socialSecurity.wtwFormatDD')}
                      <br />
                      <strong>MM</strong> – {t('applicationForm.socialSecurity.wtwFormatMM')}
                      <br />
                      <strong>F</strong> – {t('applicationForm.socialSecurity.wtwFormatF')}
                      <br />
                      <strong>L</strong> – {t('applicationForm.socialSecurity.wtwFormatL')}
                      <br />
                      <strong>XXX</strong> – {t('applicationForm.socialSecurity.wtwFormatXXX')}
                      <br />
                      <br />
                      {t('applicationForm.socialSecurity.wtwFormatExample')}
                    </div>
                  }
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </>
  );
};
